import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import Router, { useRouter } from 'next/router';
import { useRecoilState, useRecoilValue } from 'recoil';

import LoginOptionsModal from '../../components/account/login-options-modal';
import OtpLoginModal from '../../components/account/otp-login-modal';
import Layout from '../../components/flight-path/layout';
import loginIdentify from '../../helpers/segment';
import useI18n from '../../hooks/use-i18n';
import { languages, contentLanguageMap } from '../../language.config';
import { error } from '../../recoil/atoms/flightPath';
import { session } from '../../recoil/atoms/session';
import AuthRestrict from '../../utils/authRestrict';
import { LINE_LOGIN_LOCALE } from '../../utils/flightpath/constants';
import { showToastMessage } from '../../utils/flightpath/toast';
import getTranslationKeys, { PAGEID } from '../../utils/i18nRosseta';

import '../../styles/flight-path/login.scss';

const FlightPathHeader = dynamic(() => import('../../components/flight-path/header'), { ssr: false });

const Login = () => {
  const i18n = useI18n();
  const router = useRouter();
  const query = router && router.query;
  const pageMetaData = i18n.t('metadata').login_page;

  const [refreshSession, setRefreshSession] = useRecoilState(session);
  const errorState = useRecoilValue(error);
  const [loginOptions, setLoginOptions] = useState(null);

  useEffect(() => {
    if (query?.unauthorized) {
      showToastMessage(i18n.t('login_page.session_expired'), false);
      router.replace(`/${i18n.activeLocale}/login`);
    }
    setRefreshSession({ ...refreshSession, loggedOut: false });
  }, []);

  const setUserSession = (userData) => {
    loginIdentify(userData.email);
    setRefreshSession({ session: true, loaded: true, user: userData });
    if (userData?.roles.includes('CUSTOMER')) Router.push(`/${i18n.activeLocale}/flight-path/sales`);
    else if (localStorage.getItem('endpoint') === 'retainers') {
      Router.push(`/${i18n.activeLocale}/flight-path/retainers`);
    } else Router.push(`/${i18n.activeLocale}/flight-path/treatment-overview`);
  };

  return (
    <Layout
      page="login"
      description={pageMetaData.description}
      title={pageMetaData.title}
      keywords={pageMetaData.keywords}
      analyticData={{ name: 'Login', properties: {} }}
      type="login"
      pageUrl="login"
    >
      <FlightPathHeader />
      <section className="login-container">
        {loginOptions || !LINE_LOGIN_LOCALE.includes(i18n.activeLocale) ? (
          <OtpLoginModal
            setUserSession={setUserSession}
            loginOptions={loginOptions}
          />
        ) : (
          <LoginOptionsModal setLoginOptions={setLoginOptions} error={errorState} />
        )}
      </section>
    </Layout>
  );
};

export async function getStaticProps({ params }) {
  const lngDict = {};
  const pageId = PAGEID.LOGIN;
  const keys = getTranslationKeys(pageId);
  const { default: lngDictAll = {} } = await import(
    `../../locales/${contentLanguageMap[params.lng]}.json`
  );
  keys.forEach((data) => {
    lngDict[data] = lngDictAll[data];
  });
  return {
    props: { lng: params.lng, lngDict, pageId }
  };
}

export async function getStaticPaths() {
  return {
    paths: languages.map((l) => ({ params: { lng: l } })),
    fallback: false
  };
}

export default AuthRestrict(Login);
